import { useState, useEffect, useRef } from 'react';
import { conditionsAPI, seriesAPI, divisionsAPI, teamsAPI, groundsAPI } from '../../services/api';
import { PageHeader, StatCards, SectionTitle, Field, Spinner, EmptyState, DayPicker } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

// ─── Condition type config (ordered correctly) ─────────────────────────────
const SECTION_TYPES = [
  { key: 'game_day',     label: 'C1 · Game Day Rules',          color: 'var(--accent)' },
  { key: 'time_slot',    label: 'C2 · Time Slot Rules',       color: 'var(--blue)' },
  { key: 'home_away',    label: 'C3 · Home/Away Balance',       color: 'var(--purple)' },
  { key: 'bye',          label: 'C4 · Bye Rules',            color: 'var(--gold)' },
  { key: 'ground_rule',  label: 'C5 · Ground Rules',          color: 'var(--blue)' },
  { key: 'matchup',      label: 'C6 · Special Matchup Rules',  color: 'var(--purple)' },
  { key: 'team_specific',    label: 'C7 · Division & Team Overrides', color: 'var(--mid)' },
  { key: 'team_ground_limit', label: 'C8 · Team–Ground Limits',        color: 'var(--purple)' },
  { key: 'pool_format',      label: 'C9 · Pool Format Rules',          color: 'var(--gold)' },
  { key: 'ground_booking',   label: 'C10 · Home Ground Assignments',    color: 'var(--accent)' },
  { key: 'fixed_game_date',  label: 'C11 · Fixed Game Date Rules',      color: 'var(--red)' },
  { key: 'free_text',        label: 'C12+ · Notes & Reference Rules',   color: 'var(--dim)' },
];

// ─── Team autocomplete ──────────────────────────────────────────────────────
function TeamAutocomplete({ teams, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const found = teams.find(t => t.id === value);
    setSelected(found || null);
    setQuery(found ? found.name : '');
  }, [value, teams]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.length > 0
    ? teams.filter(t => t.name.toLowerCase().includes(query.toLowerCase()) || t.short_name?.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : [];

  const select = (team) => { onChange(team.id); setQuery(team.name); setSelected(team); setOpen(false); };
  const clear = () => { onChange(''); setQuery(''); setSelected(null); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="input" value={query} placeholder="Type team name…"
          onChange={e => { setQuery(e.target.value); setSelected(null); onChange(''); setOpen(true); }}
          onFocus={() => { if (query) setOpen(true); }}
          style={{ flex: 1 }} />
        {selected && <button type="button" className="btn btn-ghost" style={{ padding: '0 10px', fontSize: 12 }} onClick={clear}>✕</button>}
      </div>
      {open && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-lg)', marginTop: 4, overflow: 'hidden' }}>
          {filtered.map(t => (
            <div key={t.id} onMouseDown={() => select(t)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ fontWeight: 700, minWidth: 48, fontSize: 11, color: 'var(--accent)', fontFamily: "'Roboto Mono', monospace" }}>{t.short_name}</span>
              <span>{t.name}</span>
            </div>
          ))}
        </div>
      )}
      {selected && <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4 }}>✓ {selected.short_name} selected</div>}
    </div>
  );
}

// ─── Ground autocomplete ────────────────────────────────────────────────────
function GroundAutocomplete({ grounds, value, onChange }) {
  const [query, setQuery]     = useState('');
  const [open, setOpen]       = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const found = grounds.find(g => g.id === value);
    setSelected(found || null);
    setQuery(found ? found.name : '');
  }, [value, grounds]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.length > 0
    ? grounds.filter(g => g.name.toLowerCase().includes(query.toLowerCase()) || g.short_name?.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : grounds.slice(0, 8);

  const select = (g) => { onChange(g.id); setQuery(g.name); setSelected(g); setOpen(false); };
  const clear  = () => { onChange(''); setQuery(''); setSelected(null); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="input" value={query} placeholder="Type ground name…"
          onChange={e => { setQuery(e.target.value); setSelected(null); onChange(''); setOpen(true); }}
          onFocus={() => setOpen(true)}
          style={{ flex: 1 }} />
        {selected && <button type="button" className="btn btn-ghost" style={{ padding: '0 10px', fontSize: 12 }} onClick={clear}>✕</button>}
      </div>
      {open && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-lg)', marginTop: 4, overflow: 'hidden' }}>
          {filtered.map(g => (
            <div key={g.id} onMouseDown={() => select(g)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ fontWeight: 700, minWidth: 40, fontSize: 11, color: g.ground_type === 'premier' ? 'var(--gold)' : 'var(--blue)', fontFamily: "'Roboto Mono', monospace" }}>
                {g.ground_type === 'premier' ? '★' : '○'}
              </span>
              <span style={{ flex: 1 }}>{g.name}</span>
              <span style={{ fontSize: 10, color: 'var(--mid)' }}>{g.city}</span>
              {g.ground_type === 'premier' && <span className="tag tag-gold" style={{ fontSize: 10 }}>Premier</span>}
            </div>
          ))}
        </div>
      )}
      {selected && (
        <div style={{ fontSize: 11, color: selected.ground_type === 'premier' ? 'var(--gold)' : 'var(--accent)', marginTop: 4 }}>
          ✓ {selected.name} · {selected.max_concurrent || 1} concurrent game{(selected.max_concurrent || 1) > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

// Multi-select ground picker — for C5 Ground Rules (apply one rule to many grounds at once)
function GroundMultiSelect({ grounds, value = [], onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = grounds.filter(g =>
    !value.includes(g.id) &&
    (query.length === 0 || g.name.toLowerCase().includes(query.toLowerCase()))
  ).slice(0, 10);

  const toggle  = (g) => { onChange([...value, g.id]); setQuery(''); setOpen(false); };
  const remove  = (id) => onChange(value.filter(v => v !== id));
  const addAll  = (type) => { const ids = grounds.filter(g => g.ground_type === type).map(g => g.id); onChange([...new Set([...value, ...ids])]); };

  const selectedGrounds = value.map(id => grounds.find(g => g.id === id)).filter(Boolean);

  return (
    <div ref={ref}>
      {/* Selected chips */}
      {selectedGrounds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {selectedGrounds.map(g => (
            <span key={g.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '3px 8px 3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: g.ground_type === 'premier' ? 'color-mix(in srgb, var(--gold) 15%, transparent)' : 'color-mix(in srgb, var(--blue) 12%, transparent)',
              border: `1px solid ${g.ground_type === 'premier' ? 'var(--gold)' : 'var(--blue)'}`,
              color: g.ground_type === 'premier' ? 'var(--gold)' : 'var(--blue)',
            }}>
              {g.ground_type === 'premier' ? '★ ' : ''}{g.name}
              <button type="button" onClick={() => remove(g.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 2px', fontSize: 13, lineHeight: 1, opacity: 0.7 }}>✕</button>
            </span>
          ))}
          <button type="button" onClick={() => onChange([])}
            style={{ fontSize: 11, color: 'var(--dim)', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 6px' }}>
            Clear all
          </button>
        </div>
      )}
      {/* Quick-add buttons */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
          onClick={() => addAll('premier')}>+ All Premier grounds</button>
        <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
          onClick={() => onChange(grounds.map(g => g.id))}>+ All grounds</button>
      </div>
      {/* Search input */}
      <div style={{ position: 'relative' }}>
        <input className="input" value={query}
          placeholder={value.length === 0 ? 'Type to search grounds…' : 'Add more grounds…'}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          style={{ width: '100%' }} />
        {open && filtered.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-lg)', marginTop: 4, overflow: 'hidden' }}>
            {filtered.map(g => (
              <div key={g.id} onMouseDown={() => toggle(g)}
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ fontSize: 11, color: g.ground_type === 'premier' ? 'var(--gold)' : 'var(--blue)' }}>
                  {g.ground_type === 'premier' ? '★' : '○'}
                </span>
                <span style={{ flex: 1 }}>{g.name}</span>
                <span style={{ fontSize: 10, color: 'var(--mid)' }}>{g.city}</span>
                {g.ground_type === 'premier' && <span className="tag tag-gold" style={{ fontSize: 10 }}>Premier</span>}
              </div>
            ))}
          </div>
        )}
      </div>
      {value.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 5 }}>
          {value.length} ground{value.length !== 1 ? 's' : ''} selected — the same rule will be saved for each
        </div>
      )}
    </div>
  );
}

export default function ConditionsPage() {
  const { canEdit } = useAuthStore();
  const [seriesList, setSeriesList] = useState([]);
  const [divisions, setDivisions]   = useState([]);
  const [teams, setTeams]           = useState([]);
  const [grounds, setGrounds]       = useState([]);
  const [conditions, setConditions] = useState([]);
  const [selSeries, setSelSeries]   = useState('');
  const [selDivision, setSelDivision] = useState('');
  const [activeSection, setActiveSection] = useState('game_day');
  const [loading, setLoading]       = useState(false);
  const [view, setView]             = useState('list'); // 'list' | 'edit'

  // Structured fields
  const [playDays, setPlayDays]               = useState([]);
  const [gamesPerDay, setGamesPerDay]         = useState('1');
  const [allowedTimes, setAllowedTimes]       = useState([]); // [{time:'', label:''}]
  const [homeAwayHome, setHomeAwayHome]       = useState('');
  const [homeAwayAway, setHomeAwayAway]       = useState('');
  const [homeAwayNote, setHomeAwayNote]       = useState('');
  // Per-division overrides: { [divisionId]: { home, away, note } }
  const [homeAwayOverrides, setHomeAwayOverrides] = useState({});
  const [minByes, setMinByes]                 = useState('1');
  const [maxByes, setMaxByes]                 = useState('2');

  // Free entry
  const [newGroundRule, setNewGroundRule]     = useState('');
  const EMPTY_GR = { ground_ids: [], max_games_per_day: '', allowed_times: '', division_restriction: 'none', premier_priority: false, min_games_other_divs: 1, allowed_division_ids: [], custom_rule: '' };
  const [newStructuredGR, setNewStructuredGR] = useState(EMPTY_GR);
  const [editingGRId, setEditingGRId] = useState(null); // ground_rule id being edited
  const [newFreeText, setNewFreeText]         = useState('');
  const [newTeamOverride, setNewTeamOverride] = useState({ scope: 'division', division_id: '', team_id: '', override_type: 'Time', slots: [], rule: '' });
  const EMPTY_TGL = { team_id: '', ground_id: '', max_times: '1', division_id: '', note: '' };
  const [newTeamGroundLimit, setNewTeamGroundLimit] = useState(EMPTY_TGL);
  const EMPTY_PF = { division_id: '', pool: '', league_games: '', advancing_teams: '1', seeded_game: false, note: '' };
  const [newPoolFormat, setNewPoolFormat] = useState(EMPTY_PF);
  const EMPTY_GB = { team_id: '', ground_id: '', games_count: '3', division_id: '', blocked_dates: '', note: '' };
  const EMPTY_FGD = { division_id: '', date: '', note: '' };
  const [newGroundBooking, setNewGroundBooking] = useState(EMPTY_GB);
  const [newFixedGameDate, setNewFixedGameDate] = useState(EMPTY_FGD);
  const [newTimeEntry, setNewTimeEntry]       = useState('');
  const [saving, setSaving]                   = useState(false);

  useEffect(() => { loadSeries(); groundsAPI.list().then(r => setGrounds(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (selSeries) { loadDivisions(selSeries); loadTeams(selSeries); loadConditions(); } }, [selSeries]);
  useEffect(() => { if (selSeries) loadConditions(); }, [selDivision]);

  const loadSeries = async () => {
    try {
      const { data } = await seriesAPI.list();
      setSeriesList(data);
      // No auto-select — user must explicitly choose a series
    } catch { toast.error('Failed to load series'); }
  };
  const loadDivisions = async (sid) => { try { const { data } = await divisionsAPI.list(sid); setDivisions(data); } catch {} };
  const loadTeams     = async (sid) => { try { const { data } = await teamsAPI.list({ series_id: sid }); setTeams(data); } catch {} };

  const loadConditions = async () => {
    setLoading(true);
    try {
      const params = { series_id: selSeries };
      if (selDivision) params.division_id = selDivision;
      const { data } = await conditionsAPI.list(params);
      setConditions(data);
      // Reset ALL structured state first so stale values don't persist across series/division switches
      setPlayDays([]);
      setGamesPerDay('1');
      setAllowedTimes([]);
      setHomeAwayHome('');
      setHomeAwayAway('');
      setHomeAwayNote('');
      setMinByes('1');
      setMaxByes('2');
      data.forEach(c => {
        if (c.condition_type === 'game_day')      setPlayDays(c.value?.days || []);
        if (c.condition_type === 'games_per_day') setGamesPerDay(String(c.value?.max || 1));
        if (c.condition_type === 'time_slot')     setAllowedTimes(c.value?.slots || []);
        if (c.condition_type === 'home_away') {
          if (c.division_id) {
            // Per-division override
            setHomeAwayOverrides(prev => ({
              ...prev,
              [c.division_id]: {
                home: String(c.value?.home || ''),
                away: String(c.value?.away || ''),
                note: c.value?.note || '',
              }
            }));
          } else {
            // Legacy series-wide (keep for backwards compat display)
            setHomeAwayHome(String(c.value?.home || ''));
            setHomeAwayAway(String(c.value?.away || ''));
            setHomeAwayNote(c.value?.note || '');
          }
        }
        if (c.condition_type === 'bye_min') setMinByes(String(c.value?.min || 1));
        if (c.condition_type === 'bye_max') setMaxByes(String(c.value?.max || 2));
      });
    } catch { toast.error('Failed to load conditions'); }
    finally { setLoading(false); }
  };

  const saveStructured = async () => {
    setSaving(true);
    try {
      const base = { series_id: selSeries, division_id: selDivision || null };
      const toSave = [
        { ...base, condition_type: 'game_day',      condition_key: 'C1',  label: 'Games Played On',        value: { days: playDays } },
        { ...base, condition_type: 'games_per_day', condition_key: 'C2',  label: 'Games Per Team Per Day', value: { max: parseInt(gamesPerDay) } },
        { ...base, condition_type: 'time_slot',     condition_key: 'C3',  label: 'Allowed Game Times',     value: { slots: allowedTimes } },
        // home_away saved per-division in C4 section below (not in saveStructured)
        { ...base, condition_type: 'bye_min',       condition_key: 'C9',  label: 'Min Byes Per Team',      value: { min: parseInt(minByes) } },
        { ...base, condition_type: 'bye_max',       condition_key: 'C10', label: 'Max Byes Per Team',      value: { max: parseInt(maxByes) } },
      ];
      for (const c of toSave) {
        const existing = conditions.find(x => x.condition_key === c.condition_key && x.series_id === selSeries);
        if (existing) await conditionsAPI.update(existing.id, c);
        else await conditionsAPI.create(c);
      }
      toast.success('Conditions saved');
      loadConditions();
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const addFreeEntry = async (type, value, label, extra = {}) => {
    try {
      const existing = conditions.filter(c => c.condition_type === type && c.series_id === selSeries);
      await conditionsAPI.create({
        series_id: selSeries, division_id: selDivision || null,
        condition_type: type, condition_key: `${type.slice(0,2).toUpperCase()}${existing.length + 1}`,
        label, value: { text: value, ...extra }, sort_order: existing.length + 1
      });
      toast.success('Condition added');
      loadConditions();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to add'); }
  };

  const deleteCondition = async (id) => {
    try { await conditionsAPI.delete(id); toast.success('Removed'); loadConditions(); }
    catch { toast.error('Failed to remove'); }
  };

  // Convert HH:MM (24h) → display label e.g. "12:30 PM"
  const formatTimeLabel = (hhmm) => {
    if (!hhmm) return hhmm;
    const [hStr, mStr] = hhmm.split(':');
    const h = parseInt(hStr, 10);
    const m = mStr || '00';
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${m} ${suffix}`;
  };

  const addTime = () => {
    const t = newTimeEntry.trim();
    if (!t) return toast.error('Pick a time first');
    if (!/^\d{1,2}:\d{2}$/.test(t)) return toast.error('Invalid time format');
    if (!allowedTimes.includes(t)) setAllowedTimes(p => [...p, t]);
    setNewTimeEntry('');
  };
  const removeTime = (t) => setAllowedTimes(p => p.filter(x => x !== t));

  const groundRules       = conditions.filter(c => c.condition_type === 'ground_rule');
  const freeTextConds     = conditions.filter(c => c.condition_type === 'free_text');
  const teamSpecific      = conditions.filter(c => c.condition_type === 'team_specific');
  const matchupRules      = conditions.filter(c => c.condition_type === 'matchup');
  const teamGroundLimits  = conditions.filter(c => c.condition_type === 'team_ground_limit');
  const poolFormats       = conditions.filter(c => c.condition_type === 'pool_format');
  const groundBookings    = conditions.filter(c => c.condition_type === 'ground_booking');
  const fixedGameDates    = conditions.filter(c => c.condition_type === 'fixed_game_date');
  const condCount = (key) => {
    if (key === 'game_day')
      return conditions.filter(c => c.condition_type === 'game_day' || c.condition_type === 'games_per_day').length;
    if (key === 'bye')
      return conditions.filter(c => c.condition_type === 'bye_min' || c.condition_type === 'bye_max').length;
    if (key === 'time_slot')
      return allowedTimes.length; // reflect in-memory state, not just saved DB rows
    if (key === 'team_ground_limit') return teamGroundLimits.length;
    if (key === 'pool_format')       return poolFormats.length;
    if (key === 'ground_booking')    return groundBookings.length;
    return conditions.filter(c => c.condition_type === key).length;
  };

  // ── Stat counts ────────────────────────────────────────────────────────────
  const totalRules = conditions.length;
  const hasGameDay = conditions.some(c => c.condition_type === 'game_day');
  const hasHA      = conditions.some(c => c.condition_type === 'home_away');
  const cards = [
    { label: 'Total Conditions', value: totalRules,          sub: selDivision ? 'This division' : 'This series', color: 'var(--accent)' },
    { label: 'Game Day',         value: hasGameDay ? playDays.length + ' days' : '—', sub: 'C1 play days set',  color: 'var(--blue)' },
    { label: 'Time Slots',       value: allowedTimes.length || '—', sub: 'C2 slots configured',                  color: 'var(--blue)' },
    { label: 'Home/Away',        value: divisions.length > 0 ? `${Math.ceil(parseInt(divisions[0]?.games_per_team||0)/2)}H/${Math.floor(parseInt(divisions[0]?.games_per_team||0)/2)}A` : '—', sub: 'C3 auto per division',  color: 'var(--purple)' },
  ];

  // ─── LIST VIEW ──────────────────────────────────────────────────────────────
  if (view === 'list') {
    // ── Gate: must select series first ──
    if (!selSeries) {
      return (
        <div>
          <PageHeader icon="⧉" title="Conditions"
            subtitle="Scheduling rules and constraints per series / division." />
          <div className="card" style={{ padding: 48, textAlign: 'center', maxWidth: 520, margin: '0 auto' }}>
            <div style={{ fontSize: 40, marginBottom: 14 }}>⧉</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Select a Series to Begin</div>
            <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 24, lineHeight: 1.6 }}>
              Conditions are configured per series. Choose the series you want to view or edit — then optionally narrow by division.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 320, margin: '0 auto' }}>
              <Field label="Series" style={{ margin: 0, textAlign: 'left' }}>
                <select className="input select" value={selSeries}
                  onChange={e => { setSelSeries(e.target.value); setSelDivision(''); }}
                  style={{ width: '100%' }}>
                  <option value="">— Choose a series —</option>
                  {seriesList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.season_year})</option>)}
                </select>
              </Field>
              {selSeries && divisions.length > 0 && (
                <Field label="Division" note="Optional — leave blank for series-wide" style={{ margin: 0, textAlign: 'left' }}>
                  <select className="input select" value={selDivision}
                    onChange={e => setSelDivision(e.target.value)}
                    style={{ width: '100%' }}>
                    <option value="">All Divisions</option>
                    {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </Field>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <PageHeader icon="⧉" title="Conditions"
          subtitle="Scheduling rules and constraints per series / division."
          actions={canEdit() && <button className="btn btn-primary" onClick={() => setView('edit')}><span style={{ display:'inline-block', transform:'scaleX(-1)' }}>✎</span> Edit Conditions</button>}
        />

        {/* Series/Division selectors — always visible once series is chosen */}
        <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Field label="Series" style={{ margin: 0 }}>
            <select className="input select" style={{ width: 200 }} value={selSeries} onChange={e => { setSelSeries(e.target.value); setSelDivision(''); }}>
              <option value="">— Change series —</option>
              {seriesList.map(s => <option key={s.id} value={s.id}>{s.short_name} {s.season_year}</option>)}
            </select>
          </Field>
          <Field label="Division" style={{ margin: 0 }}>
            <select className="input select" style={{ width: 180 }} value={selDivision} onChange={e => setSelDivision(e.target.value)}>
              <option value="">All Divisions</option>
              {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
        </div>

        {/* Scope banner */}
        {!loading && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: 16, padding: '9px 14px',
            background: selDivision ? 'rgba(155,109,255,.07)' : 'rgba(79,142,247,.07)',
            border: `1px solid ${selDivision ? 'rgba(155,109,255,.2)' : 'rgba(79,142,247,.2)'}`,
            borderRadius: 8, fontSize: 12,
          }}>
            <span style={{ fontSize: 14 }}>{selDivision ? '◈' : '◉'}</span>
            <span style={{ color: 'var(--mid)' }}>Viewing conditions for:</span>
            <span style={{
              fontWeight: 700,
              color: selDivision ? 'var(--purple)' : 'var(--accent)',
              padding: '2px 10px', borderRadius: 20,
              background: selDivision ? 'rgba(155,109,255,.12)' : 'rgba(79,142,247,.12)',
              border: `1px solid ${selDivision ? 'rgba(155,109,255,.3)' : 'rgba(79,142,247,.3)'}`,
            }}>
              {selDivision
                ? `${divisions.find(d => d.id === selDivision)?.name || 'Division'} only`
                : 'Entire Series (all divisions)'}
            </span>
            {selDivision && (
              <span style={{ fontSize: 11, color: 'var(--dim)', fontStyle: 'italic' }}>
                Series-wide conditions also apply
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div className="card" style={{ padding: 40 }}>
            <EmptyState icon="⧉" title="No conditions yet"
              description="Add scheduling rules and constraints for this series."
              action={canEdit() && <button className="btn btn-primary" onClick={() => setView('edit')}>+ Add Conditions</button>}
            />
          </div>
        ) : (
          <>
            <StatCards cards={cards} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {SECTION_TYPES.map(({ key, label, color }) => {
                const items = conditions.filter(c => c.condition_type === key || (key === 'game_day' && c.condition_type === 'games_per_day'));
                if (items.length === 0 && !['game_day','time_slot','home_away','bye','pool_format','ground_booking'].includes(key)) return null;
                return (
                  <div key={key} className="card" style={{ padding: 16, borderLeft: `3px solid ${color}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color }}>{label}</div>
                        {/* Scope pill — show division name if any item in this section is division-specific */}
                        {(() => {
                          const divSpecific = items.filter(i => i.division_id);
                          const seriesWide  = items.filter(i => !i.division_id);
                          if (divSpecific.length > 0 && seriesWide.length > 0) {
                            return <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(155,109,255,.1)', color: 'var(--purple)', border: '1px solid rgba(155,109,255,.25)', fontWeight: 600 }}>Mixed scope</span>;
                          }
                          if (divSpecific.length > 0) {
                            const divName = divisions.find(d => d.id === divSpecific[0].division_id)?.name || 'Division';
                            return <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(155,109,255,.1)', color: 'var(--purple)', border: '1px solid rgba(155,109,255,.25)', fontWeight: 600 }}>◈ {divName}</span>;
                          }
                          // Only show "series-wide" pill when a division is selected (to clarify the override)
                          if (selDivision && seriesWide.length > 0) {
                            return <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(79,142,247,.1)', color: 'var(--accent)', border: '1px solid rgba(79,142,247,.25)', fontWeight: 600 }}>◉ Series-wide</span>;
                          }
                          return null;
                        })()}
                      </div>
                      {canEdit() && <button className="btn btn-ghost btn-sm" onClick={() => { setActiveSection(key); setView('edit'); }}>Edit</button>}
                    </div>
                    {key === 'game_day' && (
                      <div style={{ fontSize: 13, color: 'var(--mid)' }}>
                        {playDays.length > 0 ? <><strong>Play days:</strong> {playDays.join(', ')}</> : <span style={{ color: 'var(--dim)' }}>Not configured</span>}
                        {gamesPerDay && <span style={{ marginLeft: 16 }}><strong>Max per day:</strong> {gamesPerDay}</span>}
                      </div>
                    )}
                    {key === 'time_slot' && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {allowedTimes.length > 0
                          ? allowedTimes.map(t => <span key={t} className="tag tag-blue">{t}</span>)
                          : <span style={{ fontSize: 12, color: 'var(--dim)' }}>Not configured</span>}
                      </div>
                    )}
                    {key === 'home_away' && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {divisions.map(div => {
                          const g = parseInt(div.games_per_team || 0, 10);
                          const override = conditions.find(c => c.condition_type === 'home_away' && c.division_id === div.id);
                          const h = override ? override.value?.home : (g > 0 ? Math.ceil(g / 2) : null);
                          const a = override ? override.value?.away : (g > 0 ? Math.floor(g / 2) : null);
                          return h != null ? (
                            <span key={div.id} style={{ fontSize: 11, background: 'rgba(155,109,255,.1)', color: 'var(--purple)', borderRadius: 4, padding: '2px 8px', fontWeight: 600 }}>
                              {div.name}: {h}H / {a}A{override ? ' ★' : ''}
                            </span>
                          ) : null;
                        })}
                        {divisions.every(d => !parseInt(d.games_per_team)) && (
                          <span style={{ color: 'var(--dim)', fontStyle: 'italic', fontSize: 12 }}>Set games/team in Divisions page</span>
                        )}
                      </div>
                    )}
                    {key === 'bye' && (
                      <div style={{ fontSize: 13, color: 'var(--mid)' }}>
                        {conditions.some(c => c.condition_type === 'bye_min' || c.condition_type === 'bye_max')
                          ? <><strong>Min:</strong> {minByes} &nbsp;<strong>Max:</strong> {maxByes}</>
                          : <span style={{ color: 'var(--dim)' }}>Not configured</span>}
                      </div>
                    )}
                    {['ground_rule','team_specific','team_ground_limit','pool_format','ground_booking','fixed_game_date','free_text','matchup'].includes(key) && (
                      items.length === 0
                        ? <span style={{ fontSize: 12, color: 'var(--dim)' }}>None added</span>
                        : items.map(r => {
                            const v = r.value || {};
                            const gnd = grounds.find(g => g.id === v.ground_id);
                            const tm  = teams.find(t => t.id === v.team_id);
                            return (
                              <div key={r.id} style={{ fontSize: 12, color: 'var(--mid)', padding: '6px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                {key === 'ground_rule' && gnd && <span style={{ fontWeight: 700, color: 'var(--text)' }}>{gnd.name}</span>}
                                {key === 'ground_rule' && v.max_games_per_day && <span className="tag tag-blue">Max {v.max_games_per_day}/day</span>}
                                {key === 'ground_rule' && v.allowed_times && <span className="tag tag-blue">{v.allowed_times}</span>}
                                {key === 'ground_rule' && v.division_restriction && v.division_restriction !== 'none' && (
                                  <span className="tag tag-gold">{v.division_restriction === 'premier_only' ? '★ Premier only' : v.division_restriction === 'standard_only' ? 'Standard only' : v.division_restriction}</span>
                                )}
                                {key === 'ground_rule' && v.custom_rule && <span style={{ fontStyle: 'italic' }}>{v.custom_rule}</span>}
                                {key === 'team_ground_limit' && (
                                  <>
                                    <span style={{ fontWeight: 700, color: 'var(--accent)', fontFamily: "'Roboto Mono',monospace", fontSize: 11 }}>{tm?.short_name || v.team_short || '?'}</span>
                                    <span style={{ color: 'var(--dim)' }}>at</span>
                                    <span style={{ fontWeight: 700, color: 'var(--text)' }}>{gnd?.name || v.ground_name || '?'}</span>
                                    <span className="tag tag-purple">max {v.max_times ?? 1}×/season</span>
                                    {v.division_id && <span className="tag tag-gold">{divisions.find(d => d.id === v.division_id)?.name || 'Div'}</span>}
                                    {v.note && <span style={{ fontStyle: 'italic', color: 'var(--dim)' }}>{v.note}</span>}
                                  </>
                                )}
                                {key === 'pool_format' && (() => {
                                  const div = divisions.find(d => d.id === v.division_id);
                                  return (
                                    <>
                                      {div && <span className="tag tag-blue">{div.name}</span>}
                                      {v.pool && <span style={{ fontWeight: 700, color: 'var(--gold)', fontFamily: "'Roboto Mono',monospace", fontSize: 12 }}>Pool {v.pool}</span>}
                                      {v.league_games && <span className="tag tag-gold">{v.league_games} league games</span>}
                                      {v.seeded_game && <span className="tag tag-gold">+ seeded final</span>}
                                      {v.advancing_teams && <span className="tag tag-purple">{v.advancing_teams} advance direct</span>}
                                      {v.note && <span style={{ fontStyle: 'italic', color: 'var(--dim)' }}>{v.note}</span>}
                                    </>
                                  );
                                })()}
                                {key === 'ground_booking' && (() => {
                                  const gnd = grounds.find(g => g.id === v.ground_id);
                                  const tm  = teams.find(t => t.id === v.team_id);
                                  const div = divisions.find(d => d.id === v.division_id);
                                  return (
                                    <>
                                      {tm && <span style={{ fontWeight: 700, color: 'var(--accent)', fontFamily: "'Roboto Mono',monospace", fontSize: 11 }}>{tm.short_name}</span>}
                                      <span style={{ color: 'var(--dim)' }}>@</span>
                                      <span style={{ fontWeight: 700, color: 'var(--text)' }}>{gnd?.name || v.ground_name || '?'}</span>
                                      {v.games_count && <span className="tag tag-blue">{v.games_count} home games</span>}
                                      {v.blocked_dates && (
                                  <span className="tag tag-red">Blocked: {(Array.isArray(v.blocked_dates) ? v.blocked_dates : [v.blocked_dates]).map(d => { const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[2]}-${m[3]}-${m[1]}` : d; }).join(', ')}</span>
                                )}
                                      {div && <span className="tag tag-gold">{div.name}</span>}
                                      {v.note && <span style={{ fontStyle: 'italic', color: 'var(--dim)' }}>{v.note}</span>}
                                    </>
                                  );
                                })()}
                                {!['ground_rule','team_ground_limit','pool_format','ground_booking'].includes(key) && (v.text || JSON.stringify(v))}
                              </div>
                            );
                          })
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── EDIT VIEW ──────────────────────────────────────────────────────────────
  if (!selSeries) {
    return (
      <div>
        <PageHeader icon="⧉" title="Edit Conditions"
          subtitle="Scheduling rules and constraints per series / division."
          actions={<button className="btn btn-ghost" onClick={() => setView('list')}>← Back</button>}
        />
        <div className="card" style={{ padding: 48, textAlign: 'center', maxWidth: 520, margin: '0 auto' }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>⧉</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Select a Series First</div>
          <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 24, lineHeight: 1.6 }}>
            Choose the series you want to edit conditions for before making changes.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 320, margin: '0 auto' }}>
            <Field label="Series" style={{ margin: 0, textAlign: 'left' }}>
              <select className="input select" value={selSeries}
                onChange={e => { setSelSeries(e.target.value); setSelDivision(''); }}
                style={{ width: '100%' }}>
                <option value="">— Choose a series —</option>
                {seriesList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.season_year})</option>)}
              </select>
            </Field>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader icon="⧉" title="Edit Conditions"
        subtitle="Scheduling rules and constraints per series / division."
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={() => setView('list')}>← Back</button>
            <button className="btn btn-primary" onClick={saveStructured} disabled={saving}>
              {saving ? <><Spinner size={14} /> Saving…</> : '✓ Save Conditions'}
            </button>
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20 }}>
        {/* Sidebar */}
        <div>
          <div className="card" style={{ padding: 16, marginBottom: 12 }}>
            <SectionTitle>Context</SectionTitle>
            <Field label="Series">
              <select className="input select" value={selSeries} onChange={e => { setSelSeries(e.target.value); setSelDivision(''); }}>
                <option value="">— Change series —</option>
                {seriesList.map(s => <option key={s.id} value={s.id}>{s.short_name} {s.season_year}</option>)}
              </select>
            </Field>
            <Field label="Division">
              <select className="input select" value={selDivision} onChange={e => setSelDivision(e.target.value)}>
                <option value="">All Divisions</option>
                {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
            {/* Scope indicator */}
            <div style={{
              marginTop: 4, padding: '8px 10px', borderRadius: 7,
              background: selDivision ? 'rgba(155,109,255,.08)' : 'rgba(79,142,247,.08)',
              border: `1px solid ${selDivision ? 'rgba(155,109,255,.2)' : 'rgba(79,142,247,.2)'}`,
            }}>
              <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.6px', color: 'var(--dim)', marginBottom: 4 }}>Editing scope</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: selDivision ? 'var(--purple)' : 'var(--accent)' }}>
                {selDivision
                  ? `◈ ${divisions.find(d => d.id === selDivision)?.name || 'Division'} only`
                  : '◉ Entire Series'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 3 }}>
                {selDivision
                  ? 'Rules saved here apply to this division only and override series-wide rules.'
                  : 'Rules saved here apply to all divisions in this series.'}
              </div>
            </div>
          </div>
          <div className="card" style={{ padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', padding: '0 4px 8px', textTransform: 'uppercase', letterSpacing: 1 }}>Condition Types</div>
            {SECTION_TYPES.map(({ key, label, color }) => (
              <div key={key} onClick={() => setActiveSection(key)}
                style={{ padding: '8px 10px', borderRadius: 6, marginBottom: 2, cursor: 'pointer', fontSize: 12,
                  fontWeight: activeSection === key ? 700 : 400,
                  color: activeSection === key ? color : 'var(--mid)',
                  background: activeSection === key ? `rgba(0,200,150,.08)` : 'transparent',
                  border: `1px solid ${activeSection === key ? color : 'transparent'}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{label}</span>
                {condCount(key) > 0 && (
                  <span style={{ fontSize: 10, background: color, color: '#fff', padding: '1px 6px', borderRadius: 10 }}>{condCount(key)}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main panel */}
        <div>
          {loading ? (
            <div className="card" style={{ padding: 60, display: 'flex', justifyContent: 'center' }}><Spinner size={28} /></div>
          ) : (
            <>
              {/* C1 – Game Day Rules */}
              {activeSection === 'game_day' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--accent)">C1 · Game Day Rules</SectionTitle>
                  <Field label="Play Days" note="Days of the week when matches are scheduled">
                    <DayPicker value={playDays} onChange={setPlayDays} />
                  </Field>
                  <Field label="C2 · Max Games Per Team Per Day">
                    <div style={{ display: 'flex', gap: 8 }}>
                      {['1','2','3'].map(n => (
                        <button key={n} type="button"
                          className={`btn btn-sm ${gamesPerDay === n ? 'btn-primary' : 'btn-ghost'}`}
                          onClick={() => setGamesPerDay(n)}>{n}</button>
                      ))}
                    </div>
                  </Field>
                  <SaveBar onSave={saveStructured} saving={saving} />
                </div>
              )}

              {/* C2 – Time Slot Rules */}
              {activeSection === 'time_slot' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--blue)">C2 · Allowed Game Times</SectionTitle>
                  <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20 }}>
                    Set the start times allowed for this series. The generator will only schedule matches at these times.
                  </p>

                  {/* Quick-add presets */}
                  <Field label="Quick Add" note="Click a common time to add it instantly">
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {[
                        { label: '9:00 AM',  value: '09:00' },
                        { label: '10:00 AM', value: '10:00' },
                        { label: '10:30 AM', value: '10:30' },
                        { label: '12:00 PM', value: '12:00' },
                        { label: '12:30 PM', value: '12:30' },
                        { label: '1:00 PM',  value: '13:00' },
                        { label: '2:00 PM',  value: '14:00' },
                        { label: '2:30 PM',  value: '14:30' },
                        { label: '4:00 PM',  value: '16:00' },
                        { label: '4:30 PM',  value: '16:30' },
                      ].map(({ label, value }) => {
                        const already = allowedTimes.includes(value);
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => { if (!already) setAllowedTimes(p => [...p, value]); }}
                            style={{
                              padding: '5px 11px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                              cursor: already ? 'default' : 'pointer', transition: 'all .12s',
                              border: `1.5px solid ${already ? 'var(--accent)' : 'var(--border)'}`,
                              background: already ? 'rgba(0,200,150,.12)' : 'var(--surface)',
                              color: already ? 'var(--accent)' : 'var(--mid)',
                            }}
                          >
                            {already ? '✓ ' : ''}{label}
                          </button>
                        );
                      })}
                    </div>
                  </Field>

                  {/* Custom time picker */}
                  <Field label="Custom Time" note="Pick any time not in the presets above">
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        className="input"
                        type="time"
                        value={newTimeEntry}
                        onChange={e => setNewTimeEntry(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addTime(); }}
                        style={{ width: 140 }}
                      />
                      <button className="btn btn-primary" onClick={addTime}>+ Add</button>
                      {newTimeEntry && (
                        <span style={{ fontSize: 12, color: 'var(--mid)', fontWeight: 600 }}>
                          = {formatTimeLabel(newTimeEntry)}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 4 }}>
                      Uses 24-hour clock internally (e.g. 4:00 PM is stored as 16:00)
                    </div>
                  </Field>

                  {/* Current slots */}
                  {allowedTimes.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                        Configured Slots ({allowedTimes.length})
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {[...allowedTimes].sort().map(t => (
                          <div key={t} style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            padding: '6px 10px', borderRadius: 6,
                            background: 'rgba(0,200,150,.1)', border: '1px solid var(--accent)',
                            fontSize: 12, fontWeight: 700, color: 'var(--accent)'
                          }}>
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{formatTimeLabel(t)}</span>
                            <span style={{ fontSize: 10, color: 'var(--dim)', fontWeight: 400 }}>({t})</span>
                            <button
                              type="button"
                              onClick={() => removeTime(t)}
                              style={{ background: 'none', border: 'none', color: 'var(--mid)', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1, marginLeft: 2 }}
                              title="Remove"
                            >✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {allowedTimes.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 8, padding: '10px 14px', background: 'var(--surface)', borderRadius: 7, border: '1px solid var(--border)' }}>
                      No time slots added yet. Add at least one time for the generator to use.
                    </div>
                  )}

                  <SaveBar onSave={saveStructured} saving={saving} />
                </div>
              )}

              {/* C4 – Home/Away Balance */}
              {activeSection === 'home_away' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--purple)">C3 · Home/Away Balance</SectionTitle>
                  <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20, lineHeight: 1.6 }}>
                    Home/away split is auto-calculated per division from <strong>games per team</strong>.
                    Override any division individually if the default split doesn't apply.
                  </p>

                  {divisions.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--dim)', padding: '10px 0' }}>
                      No divisions found for this series. Add divisions first.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {divisions.map(div => {
                        const g         = parseInt(div.games_per_team || 0, 10);
                        const autoHome  = g > 0 ? Math.ceil(g / 2)  : null;
                        const autoAway  = g > 0 ? Math.floor(g / 2) : null;
                        const override  = homeAwayOverrides[div.id] || {};
                        const hasOverride = override.home !== undefined && override.home !== '';
                        const dispHome  = hasOverride ? override.home  : (autoHome  ?? '—');
                        const dispAway  = hasOverride ? override.away  : (autoAway  ?? '—');
                        const [expanded, setExpanded] = [false, () => {}]; // controlled inline

                        return (
                          <div key={div.id} style={{
                            padding: '14px 16px', borderRadius: 8,
                            background: 'var(--surface)',
                            border: `1.5px solid ${hasOverride ? 'rgba(155,109,255,.35)' : 'var(--border)'}`,
                          }}>
                            {/* Row: division name + auto values + override toggle */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                              <div style={{ minWidth: 120 }}>
                                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>{div.name}</div>
                                <div style={{ fontSize: 10, color: 'var(--dim)' }}>{div.team_count || '?'} teams · {g || '?'} games/team</div>
                              </div>
                              {/* Home pill */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{
                                  padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 800,
                                  fontFamily: "'Roboto Mono',monospace",
                                  background: 'rgba(0,200,150,.12)', color: 'var(--accent)',
                                  border: '1px solid rgba(0,200,150,.3)',
                                }}>{dispHome}H</span>
                                <span style={{ fontSize: 11, color: 'var(--dim)' }}>/</span>
                                <span style={{
                                  padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 800,
                                  fontFamily: "'Roboto Mono',monospace",
                                  background: 'rgba(100,149,237,.12)', color: 'var(--blue)',
                                  border: '1px solid rgba(100,149,237,.3)',
                                }}>{dispAway}A</span>
                              </div>
                              {/* Auto label or override badge */}
                              {hasOverride
                                ? <span style={{ fontSize: 10, background: 'rgba(155,109,255,.12)', color: 'var(--purple)', borderRadius: 4, padding: '2px 8px', fontWeight: 600 }}>Override</span>
                                : <span style={{ fontSize: 10, color: 'var(--dim)', fontStyle: 'italic' }}>Auto-calculated</span>
                              }
                              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                {hasOverride && canEdit() && (
                                  <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--red)' }}
                                    onClick={async () => {
                                      // Delete the saved override condition
                                      const existing = conditions.find(c =>
                                        c.condition_type === 'home_away' && c.division_id === div.id
                                      );
                                      if (existing) await deleteCondition(existing.id);
                                      setHomeAwayOverrides(prev => {
                                        const next = { ...prev };
                                        delete next[div.id];
                                        return next;
                                      });
                                    }}>✕ Remove override</button>
                                )}
                                {canEdit() && (
                                  <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                                    onClick={() => setHomeAwayOverrides(prev => ({
                                      ...prev,
                                      [div.id]: prev[div.id]?._editing
                                        ? { ...prev[div.id], _editing: false }
                                        : {
                                            home: String(hasOverride ? override.home : (autoHome ?? '')),
                                            away: String(hasOverride ? override.away : (autoAway ?? '')),
                                            note: override.note || '',
                                            _editing: true,
                                          }
                                    }))}>
                                    {override._editing ? '▲ Close' : '✎ Override'}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Inline override form */}
                            {override._editing && (
                              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <Field label="Home Games" style={{ margin: 0 }}>
                                  <input className="input" type="number" min="0" style={{ width: 80 }}
                                    value={override.home}
                                    onChange={e => setHomeAwayOverrides(prev => ({ ...prev, [div.id]: { ...prev[div.id], home: e.target.value } }))} />
                                </Field>
                                <Field label="Away Games" style={{ margin: 0 }}>
                                  <input className="input" type="number" min="0" style={{ width: 80 }}
                                    value={override.away}
                                    onChange={e => setHomeAwayOverrides(prev => ({ ...prev, [div.id]: { ...prev[div.id], away: e.target.value } }))} />
                                </Field>
                                <Field label="Note" note="Optional" style={{ margin: 0, flex: 1 }}>
                                  <input className="input" placeholder="e.g. ±1 acceptable"
                                    value={override.note}
                                    onChange={e => setHomeAwayOverrides(prev => ({ ...prev, [div.id]: { ...prev[div.id], note: e.target.value } }))} />
                                </Field>
                                <button className="btn btn-primary btn-sm" style={{ marginBottom: 2 }}
                                  onClick={async () => {
                                    if (!override.home && !override.away) return toast.error('Enter home or away games');
                                    const existing = conditions.find(c =>
                                      c.condition_type === 'home_away' && c.division_id === div.id && c.series_id === selSeries
                                    );
                                    const payload = {
                                      series_id: selSeries, division_id: div.id,
                                      condition_type: 'home_away', condition_key: 'C4',
                                      label: `${div.name} Home/Away`,
                                      value: { home: parseInt(override.home) || 0, away: parseInt(override.away) || 0, note: override.note || '' }
                                    };
                                    try {
                                      if (existing) await conditionsAPI.update(existing.id, payload);
                                      else await conditionsAPI.create(payload);
                                      toast.success(`${div.name} home/away saved`);
                                      setHomeAwayOverrides(prev => ({ ...prev, [div.id]: { ...prev[div.id], _editing: false } }));
                                      loadConditions();
                                    } catch { toast.error('Save failed'); }
                                  }}>✓ Save</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* C5 – Ground Rules */}
              {activeSection === 'ground_rule' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--blue)">C5 · Ground Rules</SectionTitle>
                  <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20 }}>
                    Set rules per ground — max games, allowed times, division access (Premier priority with min game requirements for other divs), or custom notes.
                  </p>

                  {/* Existing rules — card grid like availability page */}
                  {groundRules.length === 0
                    ? <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 16 }}>No ground rules yet.</div>
                    : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
                        {groundRules.map((r, i) => {
                          const v = r.value || {};
                          const gnd = grounds.find(g => g.id === v.ground_id);
                          const isPremier = gnd?.ground_type === 'premier';
                          const allowedDivNames = (v.allowed_division_ids || [])
                            .map(id => divisions.find(d => d.id === id)?.name).filter(Boolean);
                          return (
                            <div key={r.id} style={{
                              padding: '12px 14px', borderRadius: 8,
                              flex: '1 1 175px', minWidth: 175, maxWidth: 220,
                              background: 'var(--surface)',
                              border: `1.5px solid ${isPremier ? 'rgba(245,166,35,.35)' : 'var(--border)'}`,
                              position: 'relative',
                            }}>
                              {canEdit() && (
                                <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 2 }}>
                                  <button onClick={() => {
                                    setEditingGRId(r.id);
                                    setNewStructuredGR({
                                      ground_ids: v.ground_id ? [v.ground_id] : [],
                                      max_games_per_day: v.max_games_per_day || '',
                                      allowed_times: v.allowed_times || '',
                                      division_restriction: v.division_restriction || 'none',
                                      premier_priority: !!v.premier_priority,
                                      min_games_other_divs: v.min_games_other_divs || 1,
                                      allowed_division_ids: v.allowed_division_ids || [],
                                      custom_rule: v.custom_rule || '',
                                    });
                                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                                  }}
                                    style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: 12, padding: '2px 4px', lineHeight: 1 }}
                                    title="Edit"><span style={{ display:'inline-block', transform:'scaleX(-1)' }}>✏️</span></button>
                                  <button onClick={() => deleteCondition(r.id)}
                                    style={{ background: 'none', border: 'none', color: 'var(--dim)', cursor: 'pointer', fontSize: 13, padding: '2px 4px', lineHeight: 1 }}
                                    title="Delete">✕</button>
                                </div>
                              )}
                              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', paddingRight: 18, marginBottom: 2 }}>
                                {gnd ? gnd.name : v.ground_name || 'Ground'}
                              </div>
                              {isPremier
                                ? <div style={{ fontSize: 10, color: 'var(--gold)', fontWeight: 600, marginBottom: 6 }}>★ Premier</div>
                                : <div style={{ marginBottom: 6 }} />}
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {v.max_games_per_day && (
                                  <span style={{ fontSize: 10, background: 'rgba(100,149,237,.15)', color: 'var(--blue)', borderRadius: 4, padding: '2px 7px', fontWeight: 600 }}>
                                    Max {v.max_games_per_day}/day
                                  </span>
                                )}
                                {v.allowed_times && (
                                  <span style={{ fontSize: 10, background: 'rgba(0,200,150,.12)', color: 'var(--accent)', borderRadius: 4, padding: '2px 7px' }}>
                                    🕐 {v.allowed_times}
                                  </span>
                                )}
                                {v.premier_priority && (
                                  <span style={{ fontSize: 10, background: 'rgba(245,166,35,.15)', color: 'var(--gold)', borderRadius: 4, padding: '2px 7px', fontWeight: 700 }}>
                                    ★ Premier priority
                                  </span>
                                )}
                                {/* Allowed divisions pills — fix 2 */}
                                {v.premier_priority && allowedDivNames.length > 0
                                  ? allowedDivNames.map(n => (
                                      <span key={n} style={{ fontSize: 10, background: 'rgba(79,142,247,.12)', color: 'var(--blue)', borderRadius: 4, padding: '2px 7px', fontWeight: 600 }}>
                                        {n}
                                      </span>
                                    ))
                                  : v.premier_priority && (
                                      <span style={{ fontSize: 10, color: 'var(--dim)', fontStyle: 'italic' }}>All divs (leftover)</span>
                                    )
                                }
                                {v.premier_priority && v.min_games_other_divs > 0 && (
                                  <span style={{ fontSize: 10, background: 'rgba(155,109,255,.15)', color: 'var(--purple)', borderRadius: 4, padding: '2px 7px', fontWeight: 600 }}>
                                    Min {v.min_games_other_divs}/div
                                  </span>
                                )}
                                {!v.premier_priority && v.division_restriction && v.division_restriction !== 'none' && (
                                  <span style={{ fontSize: 10, background: 'rgba(245,166,35,.15)', color: 'var(--gold)', borderRadius: 4, padding: '2px 7px', fontWeight: 600 }}>
                                    {v.division_restriction === 'premier_only' ? '★ Premier only'
                                     : v.division_restriction === 'standard_only' ? 'Standard only'
                                     : v.division_restriction}
                                  </span>
                                )}
                                {v.custom_rule && (
                                  <span style={{ fontSize: 10, color: 'var(--mid)', fontStyle: 'italic', width: '100%', marginTop: 2 }}>{v.custom_rule}</span>
                                )}
                                {!v.max_games_per_day && !v.allowed_times && !v.premier_priority
                                  && (!v.division_restriction || v.division_restriction === 'none') && !v.custom_rule && (
                                  <span style={{ fontSize: 10, color: 'var(--dim)', fontStyle: 'italic' }}>No restrictions</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )
                  }

                  {/* Add new structured ground rule */}
                  {canEdit() && (
                    <div style={{ padding: 16, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: editingGRId ? 'var(--blue)' : 'var(--mid)', marginBottom: 12 }}>
                        {editingGRId ? <><span style={{ display:'inline-block', transform:'scaleX(-1)' }}>✏️</span> EDIT GROUND RULE</> : 'ADD GROUND RULE'}
                      </div>

                      <Field label="Grounds" required note="Select one or more — same rule applies to all">
                        <GroundMultiSelect grounds={grounds} value={newStructuredGR.ground_ids}
                          onChange={v => setNewStructuredGR(p => ({ ...p, ground_ids: v }))} />
                      </Field>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <Field label="Max Games Per Day" note="Leave blank for no limit">
                          <input className="input" type="number" min="1" max="10"
                            value={newStructuredGR.max_games_per_day}
                            onChange={e => setNewStructuredGR(p => ({ ...p, max_games_per_day: e.target.value }))}
                            placeholder="e.g. 2" style={{ width: 100 }} />
                        </Field>
                        <Field label="Allowed Times" note="Comma-separated if multiple">
                          <input className="input"
                            value={newStructuredGR.allowed_times}
                            onChange={e => setNewStructuredGR(p => ({ ...p, allowed_times: e.target.value }))}
                            placeholder="e.g. 10:30 AM, 2:30 PM" />
                        </Field>
                      </div>

                      <Field label="Division Access" note="How other divisions can use this ground">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {/* Premier Priority toggle */}
                          <div
                            onClick={() => setNewStructuredGR(p => ({ ...p, premier_priority: !p.premier_priority, division_restriction: !p.premier_priority ? 'none' : p.division_restriction }))}
                            style={{
                              padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                              border: `1.5px solid ${newStructuredGR.premier_priority ? 'var(--gold)' : 'var(--border)'}`,
                              background: newStructuredGR.premier_priority ? 'color-mix(in srgb, var(--gold) 10%, transparent)' : 'var(--surface)',
                              display: 'flex', alignItems: 'flex-start', gap: 10,
                              transition: 'all .12s',
                            }}>
                            <div style={{
                              width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
                              background: newStructuredGR.premier_priority ? 'var(--gold)' : 'var(--border)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, color: 'white', fontWeight: 800,
                            }}>{newStructuredGR.premier_priority ? '✓' : ''}</div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: newStructuredGR.premier_priority ? 'var(--gold)' : 'var(--text)' }}>
                                ★ Premier Priority Ground
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 2, lineHeight: 1.5 }}>
                                Premier division fills slots first. Leftover dates are open to Div 1, 2 &amp; 3.
                                Each other division must play at least this many games here.
                              </div>
                            </div>
                          </div>

                          {/* Premier priority expanded options */}
                          {newStructuredGR.premier_priority && (
                            <div style={{ paddingLeft: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
                              {/* Which divisions get leftover access */}
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                                  Divisions allowed on leftover dates
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  {divisions
                                    .filter(d => !d.name?.toLowerCase().includes('premier'))
                                    .map(d => {
                                      const sel = newStructuredGR.allowed_division_ids.includes(d.id);
                                      return (
                                        <div key={d.id}
                                          onClick={() => setNewStructuredGR(p => ({
                                            ...p,
                                            allowed_division_ids: sel
                                              ? p.allowed_division_ids.filter(id => id !== d.id)
                                              : [...p.allowed_division_ids, d.id]
                                          }))}
                                          style={{
                                            padding: '5px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                            border: `1.5px solid ${sel ? 'var(--blue)' : 'var(--border)'}`,
                                            background: sel ? 'color-mix(in srgb, var(--blue) 12%, transparent)' : 'transparent',
                                            color: sel ? 'var(--blue)' : 'var(--mid)',
                                            transition: 'all .12s',
                                          }}>
                                          {d.name}
                                        </div>
                                      );
                                    })}
                                </div>
                                <div style={{ fontSize: 10, color: 'var(--dim)', marginTop: 4, fontStyle: 'italic' }}>
                                  {newStructuredGR.allowed_division_ids.length === 0
                                    ? 'None selected — all non-premier divisions get access to leftover dates'
                                    : 'Only selected divisions get access. Others (e.g. Div 3) are excluded unless slots are short.'}
                                </div>
                              </div>
                              {/* Min games per allowed div */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontSize: 12, color: 'var(--mid)', fontWeight: 600 }}>Min games here per allowed division:</span>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  {[0, 1, 2, 3].map(n => (
                                    <div key={n}
                                      onClick={() => setNewStructuredGR(p => ({ ...p, min_games_other_divs: n }))}
                                      style={{
                                        width: 32, height: 32, borderRadius: 6, cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 13, fontWeight: 700,
                                        border: `1.5px solid ${newStructuredGR.min_games_other_divs === n ? 'var(--purple)' : 'var(--border)'}`,
                                        background: newStructuredGR.min_games_other_divs === n ? 'color-mix(in srgb, var(--purple) 15%, transparent)' : 'transparent',
                                        color: newStructuredGR.min_games_other_divs === n ? 'var(--purple)' : 'var(--mid)',
                                        transition: 'all .12s',
                                      }}>{n === 0 ? '—' : n}</div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Standard restriction (only shown when premier_priority is OFF) */}
                          {!newStructuredGR.premier_priority && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {[
                                { value: 'none',          label: 'No restriction',   color: 'var(--mid)' },
                                { value: 'premier_only',  label: '★ Premier only',   color: 'var(--gold)' },
                                { value: 'standard_only', label: 'Standard only',    color: 'var(--blue)' },
                                { value: 'top2_only',     label: 'Top 2 divs only',  color: 'var(--purple)' },
                              ].map(opt => (
                                <div key={opt.value}
                                  onClick={() => setNewStructuredGR(p => ({ ...p, division_restriction: opt.value }))}
                                  style={{
                                    padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                    border: `1.5px solid ${newStructuredGR.division_restriction === opt.value ? opt.color : 'var(--border)'}`,
                                    background: newStructuredGR.division_restriction === opt.value ? `color-mix(in srgb, ${opt.color} 12%, transparent)` : 'transparent',
                                    color: newStructuredGR.division_restriction === opt.value ? opt.color : 'var(--mid)',
                                    transition: 'all .12s',
                                  }}>
                                  {opt.label}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Field>

                      <Field label="Custom Rule / Note" note="Any additional rule for this ground">
                        <input className="input"
                          value={newStructuredGR.custom_rule}
                          onChange={e => setNewStructuredGR(p => ({ ...p, custom_rule: e.target.value }))}
                          placeholder="e.g. No back-to-back bookings, access before 8 AM requires approval" />
                      </Field>

                      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="btn btn-primary" onClick={async () => {
                          if (!newStructuredGR.ground_ids?.length) return toast.error('Select at least one ground');
                          if (!newStructuredGR.max_games_per_day && !newStructuredGR.allowed_times && newStructuredGR.division_restriction === 'none' && !newStructuredGR.custom_rule)
                            return toast.error('Add at least one rule for this ground');
                          // UPDATE mode
                          if (editingGRId) {
                            const gid = newStructuredGR.ground_ids[0];
                            const gnd = grounds.find(g => g.id === gid);
                            const existing = groundRules.find(r => r.id === editingGRId);
                            const value = {
                              text:                 existing?.value?.text || gnd?.name || 'Ground rule',
                              ground_id:            gid,
                              ground_name:          gnd?.name,
                              max_games_per_day:    newStructuredGR.max_games_per_day,
                              allowed_times:        newStructuredGR.allowed_times,
                              division_restriction: newStructuredGR.premier_priority ? 'none' : newStructuredGR.division_restriction,
                              premier_priority:     newStructuredGR.premier_priority,
                              min_games_other_divs: newStructuredGR.premier_priority ? (newStructuredGR.min_games_other_divs || 0) : 0,
                              allowed_division_ids: newStructuredGR.premier_priority ? newStructuredGR.allowed_division_ids : [],
                              custom_rule:          newStructuredGR.custom_rule,
                            };
                            try {
                              await conditionsAPI.update(editingGRId, {
                                condition_type: 'ground_rule', value,
                                label: existing?.label || `${gnd?.name} Rule`,
                                sort_order: existing?.sort_order || 0,
                                division_id: existing?.division_id || null,
                              });
                              toast.success('Ground rule updated');
                              setEditingGRId(null);
                              setNewStructuredGR(EMPTY_GR);
                              loadConditions();
                            } catch (err) { toast.error('Update failed'); }
                            return;
                          }
                          // ADD mode
                          for (const gid of newStructuredGR.ground_ids) {
                            const gnd = grounds.find(g => g.id === gid);
                            await addFreeEntry('ground_rule', gnd?.name || 'Ground rule', `${gnd?.name} Rule`, {
                              ground_id:            gid,
                              ground_name:          gnd?.name,
                              max_games_per_day:    newStructuredGR.max_games_per_day,
                              allowed_times:        newStructuredGR.allowed_times,
                              division_restriction: newStructuredGR.premier_priority ? 'none' : newStructuredGR.division_restriction,
                              premier_priority:     newStructuredGR.premier_priority,
                              min_games_other_divs: newStructuredGR.premier_priority ? (newStructuredGR.min_games_other_divs || 0) : 0,
                              allowed_division_ids: newStructuredGR.premier_priority ? newStructuredGR.allowed_division_ids : [],
                              custom_rule:          newStructuredGR.custom_rule,
                            });
                          }
                          setNewStructuredGR(EMPTY_GR);
                        }}>{editingGRId ? '💾 Update Ground Rule' : newStructuredGR.ground_ids.length > 1 ? `+ Add Rule to ${newStructuredGR.ground_ids.length} Grounds` : '+ Add Ground Rule'}</button>
                      {editingGRId && (
                        <button className="btn btn-ghost" style={{ fontSize: 12, marginLeft: 8 }}
                          onClick={() => { setEditingGRId(null); setNewStructuredGR(EMPTY_GR); }}>
                          Cancel Edit
                        </button>
                      )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* C9–10 – Bye Rules */}
              {activeSection === 'bye' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--gold)">C4 · Bye Rules</SectionTitle>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <Field label="C9 · Min Byes Per Team">
                      <input className="input" value={minByes} onChange={e => setMinByes(e.target.value)} placeholder="e.g. 1" type="number" min="0" style={{ width: 120 }} />
                    </Field>
                    <Field label="C10 · Max Byes Per Team">
                      <input className="input" value={maxByes} onChange={e => setMaxByes(e.target.value)} placeholder="e.g. 2" type="number" min="0" style={{ width: 120 }} />
                    </Field>
                  </div>
                  <SaveBar onSave={saveStructured} saving={saving} />
                </div>
              )}

              {/* C11 – Special Matchup Rules (read-only) */}
              {activeSection === 'matchup' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--purple)">C6 · Special Matchup Rules</SectionTitle>
                  <div style={{ padding: '10px 14px', background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.15)', borderRadius: 7, marginBottom: 16, fontSize: 12, color: 'var(--mid)' }}>
                    <strong style={{ color: 'var(--blue)' }}>ℹ Note:</strong> Matchup rules (e.g. DRCC vs SUSC only in August) are configured in the <strong>Matchup Matrix</strong> page. This panel is read-only.
                  </div>
                  {matchupRules.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 16 }}>No matchup restrictions configured yet.</div>
                  ) : matchupRules.map(r => (
                    <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)', marginBottom: 6 }}>
                      <span className="tag tag-blue mono">{r.value?.team1}</span>
                      <span style={{ fontSize: 11, color: 'var(--mid)' }}>vs</span>
                      <span className="tag tag-blue mono">{r.value?.team2}</span>
                      <span className="tag tag-purple">{r.value?.restriction}</span>
                      <span style={{ fontSize: 11, color: 'var(--dim)', marginLeft: 'auto', fontStyle: 'italic' }}>From matrix</span>
                    </div>
                  ))}
                  <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => window.location.assign('/matrix')}>→ Open Matchup Matrix</button>
                </div>
              )}

              {/* C7 – Division & Team Overrides */}
              {activeSection === 'team_specific' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--mid)">C7 · Division & Team Overrides</SectionTitle>
                  <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20, lineHeight: 1.6 }}>
                    Override global scheduling rules for a specific <strong>division</strong> or <strong>team</strong>.
                    Division overrides take priority over global rules. Team overrides take priority over division overrides.
                    <br/><span style={{ color: 'var(--accent)' }}>Example: Premier division plays at 12:30 PM &amp; 4:00 PM only (not the global 8:45 AM).</span>
                  </p>

                  {/* Existing overrides list */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                    {teamSpecific.length === 0
                      ? <div style={{ fontSize: 12, color: 'var(--dim)', padding: '8px 0' }}>No overrides yet.</div>
                      : teamSpecific.map(r => {
                          const v = r.value || {};
                          const div = divisions.find(d => d.id === v.division_id);
                          const team = teams.find(t => t.id === v.team_id);
                          const isDiv = !!v.division_id && !v.team_id;
                          const slots = Array.isArray(v.slots) ? v.slots : [];
                          return (
                            <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 14px', background: 'var(--surface)', borderRadius: 8, border: `1px solid ${isDiv ? 'rgba(155,109,255,.3)' : 'var(--border)'}` }}>
                              {isDiv
                                ? <span className="tag tag-gold" style={{ flexShrink: 0 }}>{div?.name || 'Division'}</span>
                                : <span className="tag tag-blue mono" style={{ flexShrink: 0 }}>{v.team_short || team?.short_name || '—'}</span>}
                              <span className="tag tag-purple" style={{ flexShrink: 0 }}>{v.override_type || 'Override'}</span>
                              {slots.length > 0
                                ? <span style={{ fontSize: 12, color: 'var(--accent)', fontFamily: "'Roboto Mono',monospace" }}>
                                    {slots.map(t => formatTimeLabel(t)).join(', ')}
                                    <span style={{ fontSize: 10, color: 'var(--dim)', marginLeft: 6, fontStyle: 'italic' }}>overrides C2 global</span>
                                  </span>
                                : <span style={{ fontSize: 12, color: 'var(--mid)', flex: 1 }}>{v.text || r.label}</span>}
                              {canEdit() && (
                                <button onClick={() => deleteCondition(r.id)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16, padding: '0 4px', marginLeft: 'auto', flexShrink: 0 }}>✕</button>
                              )}
                            </div>
                          );
                        })
                    }
                  </div>

                  {/* Add form */}
                  {canEdit() && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 16, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mid)' }}>ADD OVERRIDE</div>

                      {/* Scope toggle */}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.5px' }}>Apply To</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {['division', 'team'].map(s => (
                            <button key={s} type="button"
                              className={`btn btn-sm ${newTeamOverride.scope === s ? 'btn-primary' : 'btn-ghost'}`}
                              onClick={() => setNewTeamOverride(p => ({ ...p, scope: s, division_id: '', team_id: '' }))}>
                              {s === 'division' ? '📋 Division' : '👤 Team'}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Division or Team picker */}
                      {newTeamOverride.scope === 'division' ? (
                        <Field label="Division" required>
                          <select className="input select" value={newTeamOverride.division_id}
                            onChange={e => setNewTeamOverride(p => ({ ...p, division_id: e.target.value }))}>
                            <option value="">Select division…</option>
                            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                        </Field>
                      ) : (
                        <Field label="Team" required>
                          <TeamAutocomplete teams={teams} value={newTeamOverride.team_id}
                            onChange={v => setNewTeamOverride(p => ({ ...p, team_id: v }))} />
                        </Field>
                      )}

                      {/* Override type */}
                      <Field label="Override Type" required>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {['Time', 'Ground', 'Bye', 'Other'].map(t => (
                            <button key={t} type="button"
                              className={`btn btn-sm ${newTeamOverride.override_type === t ? 'btn-primary' : 'btn-ghost'}`}
                              onClick={() => setNewTeamOverride(p => ({ ...p, override_type: t, slots: [], rule: '' }))}>
                              {t}
                            </button>
                          ))}
                        </div>
                      </Field>

                      {/* Time override — structured slot picker */}
                      {newTeamOverride.override_type === 'Time' && (
                        <Field label="Allowed Time Slots" note="Only these times will be used for this division/team">
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                            {[
                              { label: '8:45 AM',  value: '08:45' },
                              { label: '12:30 PM', value: '12:30' },
                              { label: '4:00 PM',  value: '16:00' },
                              { label: '10:30 AM', value: '10:30' },
                              { label: '2:30 PM',  value: '14:30' },
                            ].map(({ label, value }) => {
                              const active = (newTeamOverride.slots || []).includes(value);
                              return (
                                <button key={value} type="button"
                                  style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                    border: `1.5px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                                    background: active ? 'rgba(0,200,150,.12)' : 'var(--surface)',
                                    color: active ? 'var(--accent)' : 'var(--mid)' }}
                                  onClick={() => setNewTeamOverride(p => ({
                                    ...p, slots: active
                                      ? p.slots.filter(s => s !== value)
                                      : [...(p.slots || []), value]
                                  }))}>
                                  {active ? '✓ ' : ''}{label}
                                </button>
                              );
                            })}
                          </div>
                          {(newTeamOverride.slots || []).length > 0 && (
                            <div style={{ fontSize: 11, color: 'var(--accent)', fontStyle: 'italic' }}>
                              Selected: {newTeamOverride.slots.map(t => formatTimeLabel(t)).join(', ')}
                            </div>
                          )}
                        </Field>
                      )}

                      {/* Non-time overrides — free text */}
                      {newTeamOverride.override_type !== 'Time' && (
                        <Field label="Rule" note="Describe the override">
                          <input className="input" placeholder="e.g. No Belle Isle, min 0 byes"
                            value={newTeamOverride.rule}
                            onChange={e => setNewTeamOverride(p => ({ ...p, rule: e.target.value }))} />
                        </Field>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="btn btn-primary" onClick={async () => {
                          const { scope, division_id, team_id, override_type, slots, rule } = newTeamOverride;
                          if (scope === 'division' && !division_id) return toast.error('Select a division');
                          if (scope === 'team' && !team_id) return toast.error('Select a team');
                          if (override_type === 'Time' && (!slots || slots.length === 0)) return toast.error('Select at least one time slot');
                          if (override_type !== 'Time' && !rule) return toast.error('Enter a rule description');

                          const div = divisions.find(d => d.id === division_id);
                          const team = teams.find(t => t.id === team_id);
                          const label = scope === 'division'
                            ? `${div?.name} — ${override_type} override`
                            : `${team?.short_name} — ${override_type} override`;

                          const payload = {
                            series_id: selSeries,
                            division_id: scope === 'division' ? division_id : null,
                            condition_type: 'team_specific',
                            condition_key: 'C7',
                            label,
                            value: {
                              scope,
                              division_id: scope === 'division' ? division_id : null,
                              division_name: div?.name || null,
                              team_id: scope === 'team' ? team_id : null,
                              team_short: team?.short_name || null,
                              override_type,
                              slots: override_type === 'Time' ? slots : [],
                              text: override_type !== 'Time' ? rule : slots.join(', '),
                            }
                          };
                          try {
                            await conditionsAPI.create(payload);
                            toast.success(`${label} saved`);
                            setNewTeamOverride({ scope: 'division', division_id: '', team_id: '', override_type: 'Time', slots: [], rule: '' });
                            loadConditions();
                          } catch { toast.error('Save failed'); }
                        }}>+ Add Override</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* C13 – Team–Ground Limits */}
              {activeSection === 'team_ground_limit' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--purple)">C8 · Team–Ground Limits</SectionTitle>
                  <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20, lineHeight: 1.6 }}>
                    Restrict how many times a specific team can play at a specific ground during the season.
                    <br />
                    <span style={{ color: 'var(--accent)' }}>Example: Premier teams may only play at Troy Ground once per season.</span>
                  </p>

                  {/* Existing rules */}
                  {teamGroundLimits.length === 0
                    ? <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 16 }}>No team–ground limits yet.</div>
                    : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                        {teamGroundLimits.map((r, i) => {
                          const v   = r.value || {};
                          const gnd = grounds.find(g => g.id === v.ground_id);
                          const tm  = teams.find(t => t.id === v.team_id);
                          const div = divisions.find(d => d.id === v.division_id);
                          return (
                            <div key={r.id} style={{ padding: '12px 14px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center' }}>
                              <span className="tag tag-purple mono" style={{ flexShrink: 0 }}>C8</span>
                              <div style={{ flex: 1, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                {/* Team or division */}
                                {tm
                                  ? <span style={{ fontWeight: 700, color: 'var(--accent)', fontFamily: "'Roboto Mono',monospace", fontSize: 12 }}>{tm.short_name}</span>
                                  : div
                                    ? <span className="tag tag-gold">{div.name} (all teams)</span>
                                    : <span style={{ color: 'var(--dim)', fontSize: 12 }}>All teams</span>
                                }
                                <span style={{ color: 'var(--mid)', fontSize: 12 }}>at</span>
                                <span style={{ fontWeight: 700, fontSize: 13 }}>{gnd?.name || v.ground_name || '—'}</span>
                                <span className="tag tag-purple">max {v.max_times ?? 1}× per season</span>
                                {v.note && <span style={{ fontSize: 12, color: 'var(--mid)', fontStyle: 'italic' }}>{v.note}</span>}
                              </div>
                              {canEdit() && (
                                <button onClick={() => deleteCondition(r.id)}
                                  style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>✕</button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  }

                  {/* Add new */}
                  {canEdit() && (
                    <div style={{ padding: 16, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mid)', marginBottom: 14 }}>ADD TEAM–GROUND LIMIT</div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <Field label="Apply To" note="Specific team, or leave blank for whole division">
                          <TeamAutocomplete teams={teams} value={newTeamGroundLimit.team_id}
                            onChange={v => setNewTeamGroundLimit(p => ({ ...p, team_id: v }))} />
                        </Field>

                        <Field label="Division" note="Applies to all teams in division if no team selected">
                          <select className="input select" value={newTeamGroundLimit.division_id}
                            onChange={e => setNewTeamGroundLimit(p => ({ ...p, division_id: e.target.value }))}>
                            <option value="">All teams / not division-specific</option>
                            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                        </Field>
                      </div>

                      <Field label="Ground" required>
                        <GroundAutocomplete grounds={grounds} value={newTeamGroundLimit.ground_id}
                          onChange={v => setNewTeamGroundLimit(p => ({ ...p, ground_id: v }))} />
                      </Field>

                      <Field label="Max Times at This Ground Per Season" note="e.g. 1 = team can only play at this ground once all season">
                        <div style={{ display: 'flex', gap: 8 }}>
                          {['1','2','3','4'].map(n => (
                            <button key={n} type="button"
                              className={`btn btn-sm ${newTeamGroundLimit.max_times === n ? 'btn-primary' : 'btn-ghost'}`}
                              onClick={() => setNewTeamGroundLimit(p => ({ ...p, max_times: n }))}>
                              {n}
                            </button>
                          ))}
                          <input className="input" type="number" min="1" max="20"
                            style={{ width: 70 }}
                            placeholder="other"
                            value={!['1','2','3','4'].includes(newTeamGroundLimit.max_times) ? newTeamGroundLimit.max_times : ''}
                            onChange={e => setNewTeamGroundLimit(p => ({ ...p, max_times: e.target.value }))} />
                        </div>
                      </Field>

                      <Field label="Note" note="Optional — e.g. 'Premier only per league rules'">
                        <input className="input" placeholder="e.g. Premier teams — Troy Ground once per season"
                          value={newTeamGroundLimit.note}
                          onChange={e => setNewTeamGroundLimit(p => ({ ...p, note: e.target.value }))} />
                      </Field>

                      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="btn btn-primary" onClick={async () => {
                          if (!newTeamGroundLimit.ground_id) return toast.error('Select a ground');
                          if (!newTeamGroundLimit.team_id && !newTeamGroundLimit.division_id)
                            return toast.error('Select a team or a division');
                          const gnd = grounds.find(g => g.id === newTeamGroundLimit.ground_id);
                          const tm  = teams.find(t => t.id === newTeamGroundLimit.team_id);
                          const div = divisions.find(d => d.id === newTeamGroundLimit.division_id);
                          const label = `${tm?.short_name || div?.name || 'All'} @ ${gnd?.name} — max ${newTeamGroundLimit.max_times}×`;
                          await addFreeEntry('team_ground_limit', label, label, {
                            team_id:     newTeamGroundLimit.team_id     || null,
                            team_short:  tm?.short_name                 || null,
                            ground_id:   newTeamGroundLimit.ground_id,
                            ground_name: gnd?.name,
                            division_id: newTeamGroundLimit.division_id || null,
                            max_times:   parseInt(newTeamGroundLimit.max_times) || 1,
                            note:        newTeamGroundLimit.note,
                          });
                          setNewTeamGroundLimit(EMPTY_TGL);
                        }}>+ Add Limit</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* C9 – Pool Format Rules */}
              {activeSection === 'pool_format' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--gold)">C9 · Pool Format Rules</SectionTitle>

                  {/* Existing rules — shown first */}
                  {poolFormats.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                      {poolFormats.map((r) => {
                        const v   = r.value || {};
                        const div = divisions.find(d => d.id === v.division_id);
                        return (
                          <div key={r.id} style={{ padding: '12px 14px', background: 'var(--surface)', borderRadius: 8, border: '1px solid rgba(245,166,35,.3)', display: 'flex', gap: 10, alignItems: 'center' }}>
                            <div style={{ flex: 1, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                              {div && <span className="tag tag-blue">{div.name}</span>}
                              <span style={{ fontWeight: 800, color: 'var(--gold)', fontFamily: "'Roboto Mono',monospace", fontSize: 14 }}>Pool {v.pool}</span>
                              {v.league_games && (
                                <span style={{ fontSize: 12, color: 'var(--mid)' }}>{v.league_games} league games</span>
                              )}
                              {v.seeded_game && <span className="tag tag-gold">+ seeded final round</span>}
                              {v.advancing_teams && (
                                <span className="tag tag-purple">{v.advancing_teams} team{v.advancing_teams > 1 ? 's' : ''} advance directly</span>
                              )}
                              {v.note && <span style={{ fontSize: 12, color: 'var(--dim)', fontStyle: 'italic' }}>{v.note}</span>}
                            </div>
                            {canEdit() && (
                              <button onClick={() => deleteCondition(r.id)}
                                style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>✕</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20, lineHeight: 1.6 }}>
                    Configure odd-pool seeded formats. When a pool has an odd number of teams, Rank 1 gets a bye on the final round and advances directly to playoffs. The remaining teams play seeded matchups (2v11, 3v10, etc.).
                    <br /><br />
                    <span style={{ color: 'var(--accent)' }}>
                      Formula: <strong>floor((g−1)×t / 2) + (t−1)/2</strong> total matches, where g = games per team, t = pool size.
                    </span>
                  </p>

                  {poolFormats.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 16 }}>No pool format rules configured. Only needed for odd-sized pools.</div>
                  )}

                  {/* Add new pool format rule */}
                  {canEdit() && (
                    <div style={{ padding: 16, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mid)', marginBottom: 14 }}>ADD POOL FORMAT RULE</div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <Field label="Division" required>
                          <select className="input select" value={newPoolFormat.division_id}
                            onChange={e => setNewPoolFormat(p => ({ ...p, division_id: e.target.value }))}>
                            <option value="">Select division…</option>
                            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                        </Field>

                        <Field label="Pool Label" note="e.g. A, B, C" required>
                          <input className="input" placeholder="A"
                            value={newPoolFormat.pool}
                            onChange={e => setNewPoolFormat(p => ({ ...p, pool: e.target.value.toUpperCase() }))}
                            style={{ maxWidth: 80, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2 }} />
                        </Field>
                      </div>

                      <Field label="League Games Per Team" note="e.g. 8 (one game per team is used for seeded round)">
                        <div style={{ display: 'flex', gap: 8 }}>
                          {['7','8','9'].map(n => (
                            <button key={n} type="button"
                              className={`btn btn-sm ${newPoolFormat.league_games === n ? 'btn-primary' : 'btn-ghost'}`}
                              onClick={() => setNewPoolFormat(p => ({ ...p, league_games: n }))}>{n}</button>
                          ))}
                          <input className="input" type="number" min="1" max="20"
                            style={{ width: 70 }} placeholder="other"
                            value={!['7','8','9'].includes(newPoolFormat.league_games) ? newPoolFormat.league_games : ''}
                            onChange={e => setNewPoolFormat(p => ({ ...p, league_games: e.target.value }))} />
                        </div>
                      </Field>

                      <Field label="Teams Advancing Directly" note="Rank 1 gets bye and advances directly to playoffs">
                        <div style={{ display: 'flex', gap: 8 }}>
                          {['1','2'].map(n => (
                            <button key={n} type="button"
                              className={`btn btn-sm ${newPoolFormat.advancing_teams === n ? 'btn-primary' : 'btn-ghost'}`}
                              onClick={() => setNewPoolFormat(p => ({ ...p, advancing_teams: n }))}>{n}</button>
                          ))}
                        </div>
                      </Field>

                      <Field label="Seeded Final Round">
                        <div style={{ display: 'flex', gap: 10 }}>
                          {[true, false].map(v => (
                            <div key={String(v)}
                              onClick={() => setNewPoolFormat(p => ({ ...p, seeded_game: v }))}
                              style={{
                                padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                border: `1.5px solid ${newPoolFormat.seeded_game === v ? 'var(--gold)' : 'var(--border)'}`,
                                background: newPoolFormat.seeded_game === v ? 'rgba(245,166,35,.12)' : 'transparent',
                                color: newPoolFormat.seeded_game === v ? 'var(--gold)' : 'var(--mid)',
                                transition: 'all .12s',
                              }}>
                              {v ? 'Yes — use seeded matchups' : 'No — standard round robin'}
                            </div>
                          ))}
                        </div>
                        {newPoolFormat.seeded_game && (
                          <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, background: 'rgba(245,166,35,.07)', border: '1px solid rgba(245,166,35,.2)', fontSize: 11, color: 'var(--gold)', lineHeight: 1.6 }}>
                            Rank 1 bye → direct to playoffs. Remaining teams: 2v(last), 3v(last−1), etc.
                          </div>
                        )}
                      </Field>

                      <Field label="Note" note="Optional">
                        <input className="input" placeholder="e.g. Pool A has 11 teams (odd) — seeded final"
                          value={newPoolFormat.note}
                          onChange={e => setNewPoolFormat(p => ({ ...p, note: e.target.value }))} />
                      </Field>

                      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="btn btn-primary" onClick={async () => {
                          if (!newPoolFormat.division_id) return toast.error('Select a division');
                          if (!newPoolFormat.pool.trim()) return toast.error('Enter a pool label (e.g. A)');
                          const div = divisions.find(d => d.id === newPoolFormat.division_id);
                          const label = `${div?.name || 'Div'} Pool ${newPoolFormat.pool} — ${newPoolFormat.seeded_game ? 'seeded' : 'standard'} format`;
                          await addFreeEntry('pool_format', label, label, {
                            division_id:     newPoolFormat.division_id,
                            pool:            newPoolFormat.pool,
                            league_games:    parseInt(newPoolFormat.league_games) || null,
                            advancing_teams: parseInt(newPoolFormat.advancing_teams) || 1,
                            seeded_game:     newPoolFormat.seeded_game,
                            note:            newPoolFormat.note,
                          });
                          setNewPoolFormat(EMPTY_PF);
                        }}>+ Add Pool Format Rule</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* C15 – Ground Bookings */}
              {activeSection === 'ground_booking' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--accent)">C10 · Home Ground Assignments</SectionTitle>
                  <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20, lineHeight: 1.6 }}>
                    Assign a team's home ground and how many home games they play there per season.
                    Grounds like Toledo and Grand Rapids where only the home team plays — add them here.
                    The Availability page will show coloured badges on those dates and exclude them from the general slot count.
                    <br />
                    <span style={{ color: 'var(--accent)' }}>
                      Example: UTCC — 3 home games at Toledo. WMCC — 3 home games at Grand Rapids (blocked June 28 for Mainers Cup).
                    </span>
                  </p>

                  {/* Existing bookings */}
                  {groundBookings.length === 0
                    ? <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 16 }}>No ground bookings yet.</div>
                    : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                        {groundBookings.map((r) => {
                          const v   = r.value || {};
                          const gnd = grounds.find(g => g.id === v.ground_id);
                          const tm  = teams.find(t => t.id === v.team_id);
                          const div = divisions.find(d => d.id === v.division_id);
                          return (
                            <div key={r.id} style={{ padding: '12px 14px', background: 'var(--surface)', borderRadius: 8, border: '1px solid rgba(0,200,150,.25)', display: 'flex', gap: 10, alignItems: 'center' }}>
                              <div style={{ flex: 1, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                {tm && (
                                  <span style={{ fontWeight: 800, color: 'var(--accent)', fontFamily: "'Roboto Mono',monospace", fontSize: 12 }}>{tm.short_name}</span>
                                )}
                                <span style={{ color: 'var(--mid)', fontSize: 12 }}>home at</span>
                                <span style={{ fontWeight: 700, fontSize: 13 }}>{gnd?.name || v.ground_name || '—'}</span>
                                {v.games_count && (
                                  <span className="tag tag-blue">{v.games_count} game{v.games_count > 1 ? 's' : ''}</span>
                                )}
                                {v.blocked_dates && (
                                  <span className="tag tag-red">Blocked: {(Array.isArray(v.blocked_dates) ? v.blocked_dates : [v.blocked_dates]).map(d => { const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[2]}-${m[3]}-${m[1]}` : d; }).join(', ')}</span>
                                )}
                                {div && <span className="tag tag-gold">{div.name}</span>}
                                {v.note && <span style={{ fontSize: 12, color: 'var(--dim)', fontStyle: 'italic' }}>{v.note}</span>}
                              </div>
                              {canEdit() && (
                                <button onClick={() => deleteCondition(r.id)}
                                  style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>✕</button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  }

                  {/* Add new ground booking */}
                  {canEdit() && (
                    <div style={{ padding: 16, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mid)', marginBottom: 14 }}>ADD GROUND BOOKING</div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <Field label="Home Team" required>
                          <TeamAutocomplete teams={teams} value={newGroundBooking.team_id}
                            onChange={v => setNewGroundBooking(p => ({ ...p, team_id: v }))} />
                        </Field>
                        <Field label="Division" note="Optional">
                          <select className="input select" value={newGroundBooking.division_id}
                            onChange={e => setNewGroundBooking(p => ({ ...p, division_id: e.target.value }))}>
                            <option value="">All / not division-specific</option>
                            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                        </Field>
                      </div>

                      <Field label="Ground" required>
                        <GroundAutocomplete grounds={grounds} value={newGroundBooking.ground_id}
                          onChange={v => setNewGroundBooking(p => ({ ...p, ground_id: v }))} />
                      </Field>

                      <Field label="Number of Home Games at This Ground" note="e.g. 3 means this team plays 3 home games here this season">
                        <div style={{ display: 'flex', gap: 8 }}>
                          {['1','2','3','4','5'].map(n => (
                            <button key={n} type="button"
                              className={`btn btn-sm ${newGroundBooking.games_count === n ? 'btn-primary' : 'btn-ghost'}`}
                              onClick={() => setNewGroundBooking(p => ({ ...p, games_count: n }))}>{n}</button>
                          ))}
                          <input className="input" type="number" min="1" max="20"
                            style={{ width: 70 }} placeholder="other"
                            value={!['1','2','3','4','5'].includes(newGroundBooking.games_count) ? newGroundBooking.games_count : ''}
                            onChange={e => setNewGroundBooking(p => ({ ...p, games_count: e.target.value }))} />
                        </div>
                      </Field>

                      <Field label="Blocked Dates for This Team at This Ground" note="Comma-separated MM-DD-YYYY, e.g. 06-28-2026, 07-05-2026">
                        <input className="input"
                          placeholder="e.g. 06-28-2026, 07-05-2026"
                          value={newGroundBooking.blocked_dates}
                          onChange={e => setNewGroundBooking(p => ({ ...p, blocked_dates: e.target.value }))} />
                        <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 4 }}>
                          Format: MM-DD-YYYY. These dates appear as blocked/red on the Availability calendar.
                        </div>
                      </Field>

                      <Field label="Note" note="Optional — e.g. 'Mainers Cup conflict', 'Field permit restriction'">
                        <input className="input"
                          placeholder="e.g. Mainers Cup — no WMCC games June 28"
                          value={newGroundBooking.note}
                          onChange={e => setNewGroundBooking(p => ({ ...p, note: e.target.value }))} />
                      </Field>

                      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="btn btn-primary" onClick={async () => {
                          if (!newGroundBooking.team_id) return toast.error('Select a home team');
                          if (!newGroundBooking.ground_id) return toast.error('Select a ground');
                          const gnd = grounds.find(g => g.id === newGroundBooking.ground_id);
                          const tm  = teams.find(t => t.id === newGroundBooking.team_id);
                          const label = `${tm?.short_name} @ ${gnd?.name} — ${newGroundBooking.games_count} home games`;
                          // Parse blocked dates into array
                          // Convert MM-DD-YYYY → YYYY-MM-DD for internal storage
                          const blockedArr = newGroundBooking.blocked_dates
                            ? newGroundBooking.blocked_dates.split(',').map(d => {
                                const s = d.trim();
                                // Accept MM-DD-YYYY, convert to YYYY-MM-DD
                                const mmddyyyy = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
                                if (mmddyyyy) return `${mmddyyyy[3]}-${mmddyyyy[1]}-${mmddyyyy[2]}`;
                                return s; // already YYYY-MM-DD or other format — pass through
                              }).filter(Boolean)
                            : [];
                          await addFreeEntry('ground_booking', label, label, {
                            team_id:       newGroundBooking.team_id,
                            team_short:    tm?.short_name,
                            ground_id:     newGroundBooking.ground_id,
                            ground_name:   gnd?.name,
                            division_id:   newGroundBooking.division_id || null,
                            games_count:   parseInt(newGroundBooking.games_count) || 1,
                            blocked_dates: blockedArr,
                            note:          newGroundBooking.note,
                          });
                          setNewGroundBooking(EMPTY_GB);
                        }}>+ Add Ground Booking</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* C16 – Fixed Game Date Rules */}
              {activeSection === 'fixed_game_date' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--red)">C11 · Fixed Game Date Rules</SectionTitle>
                  <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20, lineHeight: 1.6 }}>
                    Enforce that a specific division must have games scheduled on a particular date.
                    The schedule generator will treat this date as mandatory for that division.
                    <br />
                    <span style={{ color: 'var(--red)' }}>Example: All Premier teams must play on Aug 30, 2026.</span>
                  </p>

                  {/* Existing rules */}
                  {fixedGameDates.length === 0
                    ? <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 16 }}>No fixed game date rules yet.</div>
                    : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                        {fixedGameDates.map((r, i) => {
                          const v = r.value || {};
                          const div = divisions.find(d => d.id === v.division_id);
                          // Display date as MM-DD-YYYY
                          const dispDate = v.date
                            ? (() => { const m = v.date.match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[2]}-${m[3]}-${m[1]}` : v.date; })()
                            : '—';
                          return (
                            <div key={r.id} style={{
                              padding: '12px 14px', background: 'var(--surface)', borderRadius: 8,
                              border: '1.5px solid rgba(239,68,68,.25)', display: 'flex', gap: 10, alignItems: 'center'
                            }}>
                              <span className="tag mono" style={{ background: 'rgba(239,68,68,.12)', color: 'var(--red)', flexShrink: 0 }}>C11</span>
                              <div style={{ flex: 1, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <span style={{ fontWeight: 700, fontSize: 13, fontFamily: "'Roboto Mono',monospace", color: 'var(--red)' }}>{dispDate}</span>
                                {div && <span className="tag tag-blue">{div.name}</span>}
                                <span style={{ fontSize: 12, color: 'var(--mid)' }}>All teams must play</span>
                                {v.note && <span style={{ fontSize: 11, color: 'var(--dim)', fontStyle: 'italic' }}>{v.note}</span>}
                              </div>
                              {canEdit() && (
                                <button onClick={() => deleteCondition(r.id)}
                                  style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>✕</button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  }

                  {/* Add new fixed game date */}
                  {canEdit() && (
                    <div style={{ padding: 16, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mid)', marginBottom: 14 }}>ADD FIXED GAME DATE</div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <Field label="Division" required>
                          <select className="input select" value={newFixedGameDate.division_id}
                            onChange={e => setNewFixedGameDate(p => ({ ...p, division_id: e.target.value }))}>
                            <option value="">— Select division —</option>
                            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                        </Field>

                        <Field label="Date" note="MM-DD-YYYY" required>
                          <input className="input"
                            placeholder="e.g. 08-30-2026"
                            value={newFixedGameDate.date}
                            onChange={e => setNewFixedGameDate(p => ({ ...p, date: e.target.value }))}
                            maxLength={10} />
                        </Field>
                      </div>

                      <Field label="Note" note="Optional — reason for the fixed date">
                        <input className="input"
                          placeholder="e.g. League opening day, tournament requirement"
                          value={newFixedGameDate.note}
                          onChange={e => setNewFixedGameDate(p => ({ ...p, note: e.target.value }))} />
                      </Field>

                      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="btn btn-primary" onClick={async () => {
                          if (!newFixedGameDate.division_id) return toast.error('Select a division');
                          if (!newFixedGameDate.date.trim()) return toast.error('Enter a date');
                          // Validate and convert MM-DD-YYYY → YYYY-MM-DD
                          const mmddyyyy = newFixedGameDate.date.trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
                          if (!mmddyyyy) return toast.error('Date must be MM-DD-YYYY format, e.g. 08-30-2026');
                          const isoDate = `${mmddyyyy[3]}-${mmddyyyy[1]}-${mmddyyyy[2]}`;
                          const div = divisions.find(d => d.id === newFixedGameDate.division_id);
                          const label = `${div?.name} must play on ${newFixedGameDate.date.trim()}`;
                          await addFreeEntry('fixed_game_date', label, label, {
                            division_id: newFixedGameDate.division_id,
                            division_name: div?.name,
                            date: isoDate,
                            note: newFixedGameDate.note,
                          });
                          setNewFixedGameDate(EMPTY_FGD);
                        }}>+ Add Fixed Game Date</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* C12+ – Notes & Reference Rules */}
              {activeSection === 'free_text' && (
                <div className="card" style={{ padding: 24 }}>
                  <SectionTitle color="var(--dim)">C12+ · Notes &amp; Reference Rules</SectionTitle>
                  <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 7, background: 'rgba(245,166,35,.07)', border: '1px solid rgba(245,166,35,.2)', fontSize: 12, color: 'var(--mid)', lineHeight: 1.6 }}>
                    <strong style={{ color: 'var(--gold)' }}>ℹ️ Informational only — not enforced by the schedule generator.</strong>
                    {' '}Use the structured sections C1–C15 above for rules that must be automatically applied.
                    Use this section for human-readable reminders, league policy references, or notes that need manual review.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                    {freeTextConds.length === 0 && <div style={{ fontSize: 12, color: 'var(--dim)', padding: '8px 0' }}>No free text conditions yet.</div>}
                    {freeTextConds.map((c, i) => (
                      <div key={c.id} style={{ padding: '10px 14px', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, color: 'var(--text)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span className="tag tag-purple mono" style={{ flexShrink: 0 }}>C12+</span>
                          <span>{c.value?.text}</span>
                        </div>
                        {canEdit() && <button onClick={() => deleteCondition(c.id)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>✕</button>}
                      </div>
                    ))}
                  </div>
                  {canEdit() && (
                    <>
                      <textarea className="input" rows={3} placeholder="e.g. All Premier teams must play on 08/23/2026 — manual check required"
                        style={{ resize: 'vertical', marginBottom: 8 }}
                        value={newFreeText} onChange={e => setNewFreeText(e.target.value)} />
                      <button className="btn btn-ghost" style={{ fontSize: 12 }}
                        onClick={() => { if (newFreeText.trim()) { addFreeEntry('free_text', newFreeText, 'Note'); setNewFreeText(''); }}}>
                        + Add Note / Reference Rule
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SaveBar({ onSave, saving }) {
  return (
    <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
      <button className="btn btn-primary" onClick={onSave} disabled={saving}>
        {saving ? <><Spinner size={14} /> Saving…</> : '✓ Save'}
      </button>
    </div>
  );
}
